import React, { useState, useEffect, useRef, useCallback } from 'react';
import { isArray } from 'lodash';
import { handleError } from '../model/notification';

export function linesAsBulletpoints(value: string): JSX.Element {
  const lines = value.split('\n');
  return (
    <ul>
      {lines.map((line, index) => (
        <li key={index}>{line}</li>
      ))}
    </ul>
  );
}

export function useReloadKey(): [number, () => void] {
  const [reloadCount, setReloadCount] = useState(0);

  const reload = useCallback((): void => {
    setReloadCount((count) => count + 1);
  }, []);

  return [reloadCount, reload];
}

export function useErrorHandler(error?: Error): void {
  useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [error]);
}

export interface Selection<T> {
  all: Set<T>;
  values: T[];
  size: number;
  allSelected: boolean;
  selectionType: 'single' | 'multiple' | 'all';
  has: (item: T) => boolean;
  hasEvery: (items: T[]) => boolean;
  hasSome: (items: T[]) => boolean;
  add: (item: T | T[]) => void;
  remove: (item: T | T[]) => void;
  clear: () => void;
  toggle: (item: T | T[]) => void;
  toggleAll: (items: T[]) => void;
  selectAll: (items: T[]) => void;
}

export function wrapArray<T>(element: T | T[]): T[] {
  if (isArray(element)) {
    return element;
  } else {
    return [element];
  }
}

export function useSelection<T>(): Selection<T> {
  const [allSelected, setAllSelected] = useState(false);
  const [selection, setSelection] = useState<Set<T>>(new Set());

  function add(items): void {
    setSelection((val) => {
      wrapArray(items).forEach((item) => val.add(item));
      return new Set(val);
    });
  }

  function clear(): void {
    setSelection(new Set());
    setAllSelected(false);
  }

  let selectionType: Selection<T>['selectionType'] = 'single';
  if (allSelected) {
    selectionType = 'all';
  } else if (selection.size > 1) {
    selectionType = 'multiple';
  }

  return {
    all: selection,
    values: Array.from(selection),
    size: selection.size,
    allSelected,
    has: (item) => selection.has(item),
    hasEvery: (items) => items.every((e) => selection.has(e)),
    hasSome: (items) => items.some((e) => selection.has(e)),
    add,
    remove: (items) => {
      setSelection((val) => {
        wrapArray(items).forEach((item) => val.delete(item));
        return new Set(val);
      });
    },
    toggle: (items) => {
      setAllSelected(false);
      setSelection((val) => {
        wrapArray(items).forEach((item) => (val.has(item) ? val.delete(item) : val.add(item)));
        return new Set(val);
      });
    },
    toggleAll: (items: T[]) => {
      setAllSelected(false);
      if (selection.size === items.length) {
        clear();
      } else {
        add(items);
      }
    },
    selectAll: (items: T[]) => {
      setAllSelected(true);
      add(items);
    },
    clear,
    selectionType,
  };
}

export function useAsyncIndicator({
  handleError: shouldHandleError,
}: {
  handleError?: boolean;
} = {}): [boolean, <T>(arg: Promise<T>) => Promise<T>] {
  const [counter, setCounter] = useState(0);
  async function updater<T>(promise: Promise<T>): Promise<T> {
    setCounter((value) => value + 1);
    try {
      await promise;
    } catch (error) {
      if (shouldHandleError) {
        handleError(error);
      } else {
        throw error;
      }
    } finally {
      setCounter((value) => value - 1);
    }
    return promise;
  }
  return [counter > 0, updater];
}

type RecordWithId = Record<string, unknown> & { id: string };

export function useMap<T extends RecordWithId>(
  collection: T[]
): [Map<string, T>, (items: T | T[], clear?: boolean) => void] {
  const [map, updateMap] = useState<Map<string, T>>(new Map());

  useEffect(() => {
    const newMap = new Map<string, T>();
    collection.forEach((item) => {
      newMap.set(item.id as string, item);
    });
    updateMap(newMap);
  }, []);

  function setMap(items: T | T[], clear?: boolean): void {
    updateMap((oldMap) => {
      const newMap = new Map(oldMap);

      if (clear) {
        newMap.clear();
      }

      if (Array.isArray(items)) {
        items.forEach((item) => {
          newMap.set(item.id, item);
        });
      } else {
        newMap.set(items.id, items);
      }

      return newMap;
    });
  }

  return [map, setMap];
}

export function useId(id?: string | null): string {
  const idRef = useRef(crypto.randomUUID());
  return id ?? idRef.current;
}
